'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { restaurantsClient, commonClient, type LocationSuggestion } from '@bip/api-client';
import { RESOS_CDN_BASE } from '../config';
import { IMG } from '../../imgPrefix';

/** Slim projection of a restaurant record — everything we need to
 *  render an autocomplete row AND build the deep-link URL to its
 *  detail page. Kept as its own local type so future changes to the
 *  full `RestaurantRecord` shape don't ripple into the suggest
 *  handler. */ 
interface RestaurantSuggestion {
  id: string;
  name: string;
  slug: string;
  countrySlug: string;
  stateSlug: string;
  citySlug: string;
  image: string;
}

import { RESTAURANT_NO_IMAGE_URL } from '../config';

/** Resolve a resOS image path into a fully-qualified URL. Absolute
 *  URLs pass through; relative paths get `RESOS_CDN_BASE` (defaults
 *  to `https://resos.imgix.net`) prepended with a slash guard so we
 *  never emit `://path` or double-slash. Empty input → placeholder
 *  so the autocomplete row never shows a broken-image icon. */
function resolveRestaurantImage(url: string | null | undefined): string {
  if (!url) return RESTAURANT_NO_IMAGE_URL;
  if (url.startsWith('http')) return url;
  return `${RESOS_CDN_BASE}${url.startsWith('/') ? '' : '/'}${url}`;
}

const RECENT_QUERY_KEY = 'bip_recent_restaurant_query';
const MIN_QUERY_LEN = 3;
const MAX_PER_CATEGORY = 5;

// Mirrors the hotels SearchBar's `YYYY-MM-DD` date format — used to
// seed the hotels-domain `searches[]` blob (see `handleSearch` below)
// with a default one-night stay window.
function getCurrentAndNextDay(): { startDate: string; endDate: string } {
  const fmt = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const end = new Date(start);
  end.setDate(start.getDate() + 1);
  return { startDate: fmt(start), endDate: fmt(end) };
}

// Tiered relevance ranking used to live here (`scoreItem` / `rankItems`).
// It moved to the backend — `services/restaurants-service` now returns
// `data` already sorted by `score` DESC with alphabetical A→Z as the
// tiebreaker AND filtered to rows that actually match the query. The
// frontend just slices to `MAX_PER_CATEGORY` and renders in order.

export function RestaurantsHero() {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [location, setLocation] = useState(searchParams.get('city') || '');
  const [query, setQuery]       = useState(searchParams.get('q') || '');
  const [bannerOk, setBannerOk] = useState(true);
  // Single source of truth for which field's dropdown is open — opening
  // one automatically closes any other (no two panels visible at once).
  const [activeField, setActiveField] = useState<'location' | 'suggest' | null>(null);
  const [locationResults, setLocationResults] = useState<LocationSuggestion[]>([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const suggestOpen = activeField === 'suggest';
  const setSuggestOpen = (next: boolean) =>
    setActiveField(next ? 'suggest' : activeField === 'suggest' ? null : activeField);
  const [suggestLoading, setSuggestLoading] = useState(false);
  // Each suggestion carries the resOS id alongside the display name so
  // picking one can submit `restaurantId=<id>` (deterministic) instead
  // of a fuzzy `q=<name>` query.
  const [restaurantSuggestions, setRestaurantSuggestions] = useState<RestaurantSuggestion[]>([]);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string>('');
  // Track WHICH `SearchLocation` row backs the current `location`
  // text. Empty string means the field is either untouched or holds
  // free-typed text the user hasn't confirmed via the dropdown.
  // `handleSearch` requires this to be set — a typed keyword that
  // resembles a city but never got picked is rejected with "Location
  // is required", matching CMS behaviour (`Springfield` alone is
  // ambiguous; the CMS row disambiguates). Also set (indirectly, via
  // `resolveLocationId`) by the priority-1 localStorage restore and
  // priority-2 ipwho.org geo fallback so those auto-populated cities
  // stay submittable without a manual re-pick.
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [errors, setErrors] = useState<{ location?: string }>({});
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    // Location field pre-fill — 3-priority resolver:
    //   1. Last searched city — cross-domain `theatre-searches-city`
    //      blob seeded by sports / hotels / restaurants recent searches.
    //   2. IP-based geolocation via the common-service ipwho.org proxy
    //      (`commonClient.getGeo()` → `GET /bip-api/common/geo` →
    //      `https://api.ipwho.org/ip/{ip}?apiKey=…&get=geoLocation`).
    //      Server-side, so the ipwho API key stays off the client
    //      bundle and rate-limit / CORS constraints go through our
    //      infra.
    //   3. Hardcoded fallback — "London".
    //
    // Only runs when the URL didn't already carry `?city=` and the
    // caller didn't seed `location` some other way — an explicit URL
    // param or manual edit always beats the resolver.
    let cancelled = false;

    /** Commit the resolved city — sets local field state, writes the
     *  cross-domain `theatre-searches-city` blob, and broadcasts a
     *  `bipRestaurantsCityChanged` event so `RestaurantsList` (a
     *  sibling that reads no props) knows to fetch the new city.
     *
     *  The URL is INTENTIONALLY not touched — per CMS spec, the
     *  restaurants listing URL stays clean (`/restaurants`) instead
     *  of carrying `?city=<name>`. Cross-sibling city state travels
     *  via localStorage + the custom event on this same page load.
     *
     *  `destId` — the `SearchLocation._id` backing this city. When
     *  present, `selectedLocationId` gets set so the "must pick from
     *  dropdown" gate on `handleSearch` passes without the user having
     *  to re-select. Callers that DON'T have an id (e.g. a geo lookup
     *  that couldn't find a matching CMS row) simply omit the arg —
     *  the field pre-fills for display but the user has to pick from
     *  the dropdown before "Find Table" is enabled. */
    /** Commit a resolved city to the hero: prefill the field and
     *  broadcast the change to `RestaurantsList`. This function
     *  DELIBERATELY DOES NOT write `theatre-searches-city` — the
     *  auto-resolve reads the blob but must NEVER overwrite it with
     *  a partial payload (spec 2026-08-05: strict shape only —
     *  written exclusively on user-driven submit + on the city
     *  drilldown Sync). If the caller has a full SearchLocation
     *  row it can pass it via `writeFullBlob` (called separately
     *  from the geo/priority-2 branch when the row was fetched
     *  fresh). */
    const commitCity = (city: string, destId?: string) => {
      if (cancelled) return;
      setLocation(city);
      if (destId) setSelectedLocationId(destId);
      window.dispatchEvent(
        new CustomEvent('bipRestaurantsCityChanged', { detail: { city } }),
      );
    };

    /** Write the full-shape `theatre-searches-city` blob — ONLY when
     *  a real SearchLocation doc is available. Partial writes are
     *  forbidden by spec 2026-08-05. */
    const writeFullBlob = (doc: {
      city?: string;
      country?: string;
      destination_id?: string;
      state?: string;
      _id?: string;
    }) => {
      if (!doc?.city || !doc?._id || !doc?.destination_id) return;
      try {
        localStorage.setItem(
          'theatre-searches-city',
          JSON.stringify({
            city: doc.city,
            country: doc.country || '',
            id: doc.destination_id,
            state: doc.state || '',
            dest_id: doc._id,
          }),
        );
      } catch { /* localStorage disabled — silent */ }
    };

    const resolveLocation = async () => {
      if (searchParams.get('city') || location) return;

      // Priority 1 — last-searched city (from the cross-domain
      // `theatre-searches-city` blob). Commit UNCONDITIONALLY when
      // the blob carries a city: the earlier `cityHasRestaurants`
      // probe (`?city=<last>&limit=1`) was surfacing a phantom
      // request users hadn't asked for AND was slower than just
      // letting the listing SSR fetch decide. If the stored city
      // has zero restaurants, the empty state renders and the user
      // picks another city from the autocomplete — same graceful
      // fallback the URL-driven path already uses.
      try {
        const raw = localStorage.getItem('theatre-searches-city');
        if (raw) {
          const parsed = JSON.parse(raw) as { city?: string; dest_id?: string } | null;
          const last = parsed?.city?.trim();
          const lastId = parsed?.dest_id?.trim();
          if (last) {
            // If the blob already carries a `dest_id` (written by any
            // prior submit — sports / hotels / restaurants share this
            // shape), reuse it so submit stays unblocked. If the blob
            // is a legacy row without an id (or the id is stale), fall
            // back to a live CMS lookup; missing that too, we still
            // commit the city NAME for display but leave
            // `selectedLocationId` empty so the user has to re-pick.
            let resolvedId = lastId;
            if (!resolvedId) {
              try {
                const res = await restaurantsClient.getCityDestId(last);
                if (res?.message === 'success' && res?.data?._id) {
                  resolvedId = String(res.data._id);
                }
              } catch { /* CMS unreachable — commit name only */ }
            }
            commitCity(last, resolvedId);
            return;
          }
        }
      } catch { /* localStorage disabled — fall through */ }

      // Priority 2 — IP-based geolocation via the common-service
      // ipwho.org proxy. Hits `GET /bip-api/common/geo`, which
      // forwards to `https://api.ipwho.org/ip/{ip}?apiKey=…&get=geoLocation`
      // server-side (see `services/common-service/src/modules/geo/`).
      // Any failure (upstream 5xx, network timeout, ipwho "no match",
      // private/loopback client IP) comes back as `{ data: null }`
      // and we fall through — field stays empty, user picks manually.
      // Priority 2 accepts a geo-detected city ONLY when it exists
      // in the restaurants city collection (spec 2026-08-05). A
      // `getCityDestId` miss (no matching CMS row) → skip; falls
      // through to the London fallback below. When it hits, we
      // ALSO write the full-shape `theatre-searches-city` blob so
      // the next visit's priority-1 short-circuits on it.
      try {
        const resp = await commonClient.getGeo();
        const detected = resp?.data?.city?.trim();
        if (detected) {
          try {
            const cms = await restaurantsClient.getCityDestId(detected);
            if (cms?.message === 'success' && cms?.data?._id) {
              const doc = cms.data as {
                city?: string;
                country?: string;
                destination_id?: string;
                state?: string;
                _id?: string;
              };
              writeFullBlob(doc);
              commitCity(doc.city || detected, String(doc._id));
              return;
            }
          } catch { /* CMS unreachable — treat as a miss */ }
        }
      } catch { /* network / rate-limit — fall through to fallback */ }

      // Priority 3 — hardcoded `"London"` fallback (spec 2026-08-05
      // revised). Look up the real SearchLocation row for London so
      // submit stays unblocked (has a valid `dest_id`). If London
      // itself isn't in the CMS collection we still commit the name
      // for display — the user then has to pick from the dropdown
      // before "Find Table" is enabled, same as any other miss.
      try {
        const cms = await restaurantsClient.getCityDestId('London');
        if (cms?.message === 'success' && cms?.data?._id) {
          const doc = cms.data as {
            city?: string;
            country?: string;
            destination_id?: string;
            state?: string;
            _id?: string;
          };
          // No `writeFullBlob` for the fallback — the strict-shape
          // rule from the earlier spec still stands (only user
          // picks + drilldown syncs write the blob). Commit for
          // display only.
          commitCity(doc.city || 'London', String(doc._id));
          return;
        }
      } catch { /* CMS unreachable — commit the bare name */ }
      commitCity('London');
    };

    resolveLocation();

    // Restaurant / Cuisine field intentionally NOT restored from
    // localStorage on load — spec: "restaurant field data should reset
    // every time" the listing page opens. `RECENT_QUERY_KEY` is still
    // WRITTEN on submit (see `handleSearch`) because other flows may
    // read it, but the listing hero never reads it back into the
    // input. URL `?q=` (a deliberate deep-link / bookmarked search)
    // is still honoured via the initial `useState(searchParams.get('q'))`.

    return () => { cancelled = true; };
  }, []);

  // Debounced fetch of restaurant names for the autocomplete. City is
  // mandatory here (the field itself is disabled until `location` has
  // a value — see the input below), and every search is scoped to it
  // so "Search by Restaurant" only ever surfaces restaurants in the
  // city the customer picked, not a global name match.
  useEffect(() => {
    const trimmed = query.trim();
    const cityTrimmed = location.trim();
    if (!suggestOpen || trimmed.length < MIN_QUERY_LEN || !cityTrimmed) {
      setRestaurantSuggestions([]);
      setSuggestLoading(false);
      return;
    }
    let cancelled = false;
    setSuggestLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await restaurantsClient.searchRestaurants({
          search: trimmed,
          city: cityTrimmed,
          limit: 25,
        });
        if (cancelled) return;
        setRestaurantSuggestions(
          res.data
            .filter((r) => !!r?.id && !!r?.name && !!r?.slug)
            .map((r) => {
              // Same fallback as RestaurantCard: resOS's own `image` is
              // only populated when the venue set a primary photo through
              // resOS itself — BookItPlease-only venues carry theirs under
              // `addOns.bookItPlease.images[]` instead.
              const bipImages = (r.addOns as { bookItPlease?: { images?: { image: string }[] } } | undefined)
                ?.bookItPlease?.images;
              return {
                id: r.id,
                name: r.name,
                slug: r.slug,
                countrySlug: r.countrySlug || '',
                stateSlug: r.stateSlug || '',
                citySlug: r.citySlug || '',
                image: resolveRestaurantImage(r.image || bipImages?.[0]?.image),
              };
            }),
        );
      } catch {
        if (!cancelled) setRestaurantSuggestions([]);
      } finally {
        if (!cancelled) setSuggestLoading(false);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, suggestOpen, location]);

  // Debounced fetch for the Location autocomplete — same min-3 / 200ms
  // debounce shape as the Restaurant/Cuisine suggest.
  useEffect(() => {
    const trimmed = location.trim();
    if (activeField !== 'location' || trimmed.length < MIN_QUERY_LEN) {
      setLocationResults([]);
      setLocationLoading(false);
      return;
    }
    let cancelled = false;
    setLocationLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await restaurantsClient.getLocationSuggestions(trimmed);
        if (!cancelled) setLocationResults(res);
      } catch {
        if (!cancelled) setLocationResults([]);
      } finally {
        if (!cancelled) setLocationLoading(false);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [location, activeField]);

  // City is mandatory for the Restaurant/Cuisine field — clear out
  // whatever's in it the moment the city is emptied (typed away or
  // cleared via the × button) so a stale query can't sit in a
  // now-disabled field, and so a city re-entered afterwards always
  // starts the restaurant search fresh.
  useEffect(() => {
    if (location.trim()) return;
    setQuery('');
    setRestaurantSuggestions([]);
    setSelectedRestaurantId('');
    setSuggestOpen(false);
  }, [location]);

  // Close popovers + suggest dropdown on outside click / Escape
  useEffect(() => {
    if (!activeField) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (formRef.current && !formRef.current.contains(target as Node)) {
        setActiveField(null);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveField(null);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [activeField]);

  // The backend now returns `data` sorted by `score` DESC (tiered
  // relevance rubric — exact / starts-with / word-starts-with /
  // contains / multi-word variants — with alphabetical A→Z as the
  // tiebreaker) and filtered to rows that actually match, so the
  // frontend just caps to MAX_PER_CATEGORY and renders in order. The
  // local `rankItems`/`scoreItem` helpers stay in the file as a
  // fallback contract — if a future backend regression drops the
  // sort, callers can flip back to client-side ranking without a
  // rewrite. NOT invoked on the happy path today.
  const trimmedQuery = query.trim();
  // City is mandatory before the Restaurant/Cuisine field can be used —
  // it's disabled (not just validated on submit) until a city is entered.
  const cityEntered = !!location.trim();
  const showSuggest = cityEntered && suggestOpen && trimmedQuery.length >= MIN_QUERY_LEN;
  const rankedRestaurants: RestaurantSuggestion[] = showSuggest
    ? restaurantSuggestions.slice(0, MAX_PER_CATEGORY)
    : [];
  const totalSuggestions = rankedRestaurants.length;

  /**
   * Suggestion click — same behaviour as picking a city suggestion:
   * fill the field and close the dropdown, WITHOUT navigating anywhere.
   * (Reverses the earlier "no intermediate Find a Table step" — direct
   * navigation to the detail page from the autocomplete skipped the
   * listing entirely, so "Book Table" on a card was never reached.)
   * `selectedRestaurantId` rides along and is what `handleSearch` below
   * turns into `?restaurantId=` on submit — the listing then shows just
   * that one restaurant's card, and ITS "Book Table" link is the only
   * path to the detail page.
   */
  function pickSuggestion(item: RestaurantSuggestion) {
    setQuery(item.name);
    setSelectedRestaurantId(item.id);
    setSuggestOpen(false);
  }

  async function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setSuggestOpen(false);

    // Location gate — must be either (a) empty (nothing typed) OR
    // (b) typed WITHOUT a dropdown pick. Both fail with the same
    // "Location is required" copy per spec: a raw keyword like
    // "Sprin" is ambiguous (Springfield IL vs MO vs …) and only the
    // CMS `SearchLocation._id` disambiguates. The auto-detect +
    // localStorage restore paths pre-fill `selectedLocationId` when
    // they resolve, so those cases don't force a re-pick.
    const nextErrors: typeof errors = {};
    if (!location.trim() || !selectedLocationId) {
      nextErrors.location = 'Location is required';
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const p = new URLSearchParams();
    // City DOES ride the URL (`?city=<name>`, per BCP-1590) — needed so
    // `generateMetadata` (SSR, no localStorage access) can render the
    // city-specific title/description/canonical. The actual listing
    // fetch still reads its own `city` state from localStorage +
    // `bipRestaurantsCityChanged` below — the URL value is set to the
    // exact same resolved city name, so the two never disagree at
    // submit time; it's added just below once the CMS lookup resolves
    // the canonical casing.
    //
    // If the user picked a specific restaurant from the suggest panel,
    // forward its id (exact match) — otherwise fall back to the fuzzy
    // `q` keyword search.
    if (selectedRestaurantId) {
      p.set('restaurantId', selectedRestaurantId);
    } else if (query) {
      p.set('q', query);
    }
    try {
      if (query.trim()) localStorage.setItem(RECENT_QUERY_KEY, query.trim());
      else localStorage.removeItem(RECENT_QUERY_KEY);
    } catch {
      // localStorage can throw (private browsing, quota) — safe to ignore.
    }

    // Update the cross-domain "last searched city" blob at submit
    // time — matches the sports `CityRecentSearchSync` shape exactly
    // (`city / country / id / state / dest_id` all sourced from the
    // CMS `SearchLocation` row via `getCityDestId`). This is what
    // powers the hero's priority-1 auto-resolve on the next visit
    // AND drives any cross-domain hotels seeding downstream. Await
    // the lookup BEFORE `router.push` so the navigation doesn't
    // cancel the fetch mid-flight and leave the blob unwritten.
    const cityTrimmed = location.trim();
    if (cityTrimmed) {
      try {
        const res = await restaurantsClient.getCityDestId(cityTrimmed);
        const doc = res?.message === 'success' && res?.data ? res.data : null;
        // STRICT (spec 2026-08-05): write the theatre-searches-city
        // blob ONLY when the SearchLocation row is complete
        // (`city`, `_id`, `destination_id` all present). A partial /
        // missing row → skip the write; the next visit's priority-1
        // resolver falls through to geo instead of picking up a
        // broken blob.
        if (doc?.city && doc?._id && doc?.destination_id) {
          localStorage.setItem(
            'theatre-searches-city',
            JSON.stringify({
              city: doc.city,
              country: doc.country || '',
              id: doc.destination_id,
              state: doc.state || '',
              dest_id: doc._id,
            }),
          );
        }

        // Hotels-domain `searches[]` seeding — mirrors what the sports
        // `CityRecentSearchSync` writes on the destination page. Same
        // shape the hotels SearchBar produces on its own submit
        // (`dest_id`, `checkin`, `checkout`, `rooms`, `adults`,
        // `children`, `ratecode`), so hotels' recent-search surface
        // treats a restaurant-side seed as a first-class row. Skipped
        // when the CMS row has no `_id` (defensive — real
        // `SearchLocation` docs always carry one; a missing id
        // would mean the entry can't be turned back into a hotels
        // search query later).
        const destId = doc?._id ? String(doc._id) : '';
        if (destId) {
          const { startDate, endDate } = getCurrentAndNextDay();
          const newSearch = {
            dest_id: destId,
            checkin: startDate,
            checkout: endDate,
            rooms: 1,
            adults: '2',
            children: '',
            ratecode: '',
            // Label the entry so the hotels home "recent searches" section
            // shows the city name (it reads `_label`) — mirrors the sports
            // `CityRecentSearchSync` convention.
            _label: [doc?.city, doc?.state, doc?.country].filter(Boolean).join(', '),
          };
          const previousSearches = JSON.parse(
            localStorage.getItem('searches') || '[]',
          );
          // Dedup by `dest_id` across the WHOLE list (not just index 0) —
          // a stale entry for this destination sitting further down the
          // array must be dropped, otherwise the fresh search still ends
          // up unshifted while the old copy lingers, splitting the same
          // city across two positions instead of collapsing to index 0.
          const updated = (
            Array.isArray(previousSearches) ? previousSearches : []
          ).filter(
            (r: { dest_id?: string }) => r?.dest_id !== newSearch.dest_id,
          );
          updated.unshift(newSearch);
          if (updated.length > 3) updated.pop();
          localStorage.setItem('searches', JSON.stringify(updated));
        }
      } catch {
        // Backend / localStorage failure — DO NOT write a partial
        // blob (spec 2026-08-05). Next visit's priority-1 resolver
        // just falls through to geo instead of picking up a
        // truncated payload.
      }

      // Broadcast the new city so `RestaurantsList` (a sibling that
      // no longer reads `?city=` from the URL) knows to refetch. Same
      // event the resolver's `commitCity` uses; single listener there.
      window.dispatchEvent(
        new CustomEvent('bipRestaurantsCityChanged', { detail: { city: cityTrimmed } }),
      );
    }
    // City is omitted from `p` per the spec — the URL stays clean
    // (`/restaurants`, or `/restaurants?restaurantId=…` / `?q=…` when
    // the user picked a specific restaurant or typed a keyword).
    //
    // Skip `router.push` when the resulting URL would be identical to
    // the current one. Because city no longer travels via the URL,
    // a plain city-change submit produces the same query string as
    // before — pushing it navigates to `/restaurants` while already
    // on `/restaurants`, which with `dynamic = 'force-dynamic'` on
    // the page triggers a full server re-render and shows the
    // loading skeleton for a moment (the "search table refreshes
    // the page" symptom). The sibling `RestaurantsList` picks up
    // the new city via the `bipRestaurantsCityChanged` event
    // dispatched above, so a no-op navigation buys nothing.
    const qs = p.toString();
    if (qs !== searchParams.toString()) {
      router.push(qs ? `/restaurants?${qs}` : '/restaurants');
    }
  }

  return (
    <section className="page-container pt-12 text-center md:pt-[68px]">
      <h1 className="font-display text-[50px] font-extrabold leading-[0.95] tracking-[-0.02em] text-black md:text-[80px]">
        Your table, our passion.
      </h1>
      <div className="text-[18px] leading-snug md:mt-3 md:text-[20px]">
        The best tables in the city. Bookable all in one place.
      </div>

      {bannerOk && (
        <img
          src={`${IMG}/image/restaurants-banner.png`}
          alt="A collage of restaurant dining experiences"
          className="mx-auto my-10 w-full max-w-[1200px] md:my-[50px]"
          onError={() => setBannerOk(false)}
        />
      )}

      <form
        ref={formRef}
        id="restaurantSearch"
        onSubmit={handleSearch}
        noValidate
        className="mx-auto max-w-[680px] overflow-visible rounded-2xl border-2 border-plum-900 bg-white px-5 text-left min-[1200px]:mb-0 min-[1200px]:grid min-[1200px]:grid-cols-[minmax(180px,220px)_minmax(200px,1fr)_150px] min-[1200px]:items-center min-[1200px]:rounded-full min-[1200px]:p-1.5"
      >
        {/* Location — autocomplete from CountryCity master */}
        <label className="mb-0 r-location-filed search-field restaurant-suggest restaurant-suggest--location">
          <span className="sr-only">Search by city name</span>
          <i className="text-lg fal fa-map-marker-alt mt-1"></i>
          <input
            className="search-input-restaurant p-0"
            type="text"
            value={location}
            onChange={(e) => {
              setLocation(e.target.value);
              setActiveField('location');
              // Typing invalidates whatever CMS row was previously
              // selected — the text now diverges from `s.city`, so the
              // stored `_id` no longer describes it. Clearing here
              // forces the user back through the dropdown to
              // re-confirm before "Find Table" is enabled.
              if (selectedLocationId) setSelectedLocationId('');
              if (errors.location) setErrors((p) => ({ ...p, location: undefined }));
            }}
            onFocus={() => setActiveField('location')}
            placeholder="Search by City"
            aria-label="Search by City "
            aria-autocomplete="list"
            aria-expanded={activeField === 'location'}
            aria-invalid={!!errors.location}
          />
          {/* Clear button — visible only when the field has content.
              `onMouseDown preventDefault` keeps the focus / active
              field so the dropdown doesn't collapse before we clear. */}
          {location && (
            <button
              type="button"
              aria-label="Clear location"
              className="search-field__clear"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setLocation('');
                setLocationResults([]);
                setActiveField('location');
                // Clear the CMS row too — the field is empty, so any
                // previously selected `_id` no longer refers to
                // anything on screen. Next submit will fail the
                // "Location is required" gate until the user picks
                // again.
                setSelectedLocationId('');
                if (errors.location) setErrors((p) => ({ ...p, location: undefined }));
                // Wipe the cross-domain "last searched city" blob too —
                // without this, the next visit's priority-1 resolver
                // would auto-restore whatever city was there and the
                // user would land on the same city they just cleared,
                // which reads as "the clear button didn't work".
                // `theatre-searches-city` is written by sports /
                // hotels / restaurants flows alike (see
                // `CityRecentSearchSync`); removing it here resets
                // that shared blob for every downstream reader.
                try {
                  localStorage.removeItem('theatre-searches-city');
                } catch { /* localStorage disabled — ignore */ }
                // Broadcast the cleared state so `RestaurantsList`
                // drops its current results. The URL is intentionally
                // untouched — city no longer travels there.
                window.dispatchEvent(
                  new CustomEvent('bipRestaurantsCityChanged', { detail: { city: '' } }),
                );
                // Defensive URL cleanup — `handleSearch` below still
                // writes `?city=` (for `generateMetadata`'s SEO title),
                // so a clear right after a real search needs to strip
                // it. This uses the browser History API directly
                // (NOT `router.replace`) — the page is
                // `dynamic = 'force-dynamic'`, so a Next router
                // navigation re-renders the Server Component tree and
                // remounts this component (resetting local state and
                // re-running the mount-time location resolver, which
                // would then re-fill the field it just cleared via the
                // IP-geolocation fallback). `RestaurantsList` already
                // dropped its results from the event above — this call
                // only keeps the address bar in sync for bookmarking,
                // so it doesn't need to go through Next at all.
                if (searchParams.get('city') && typeof window !== 'undefined') {
                  const p = new URLSearchParams(searchParams.toString());
                  p.delete('city');
                  const qs = p.toString();
                  window.history.replaceState(null, '', qs ? `/restaurants?${qs}` : '/restaurants');
                }
              }}
            >
              <i className="fal fa-times" aria-hidden />
            </button>
          )}
          {errors.location && <span className="field-error mt-2">{errors.location}</span>}

          {activeField === 'location' && location.trim().length >= MIN_QUERY_LEN && (
            <div className="search-results-dropdown">
              {locationLoading && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '1.5rem 0',
                  }}
                >
                  <div className="spinner-border spinner-border-sm text-secondary" role="status" />
                </div>
              )}

              {!locationLoading && locationResults.length === 0 && (
                <span className="dropdown-item text-muted">No result found</span>
              )}

              {!locationLoading && locationResults.map((s) => {
                const sub = [s.state, s.country].filter(Boolean).join(', ');
                return (
                  <button
                    key={s._id}
                    type="button"
                    data-id={s._id}
                    className="dropdown-item ui-menu-item autocomplete-item ui-menu-item-wrapper"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setLocation(s.city);
                      // Confirm the CMS row — this is what makes
                      // "Find Table" clickable. Any prior typing had
                      // cleared it; picking from the dropdown is the
                      // only path back.
                      setSelectedLocationId(s._id);
                      setActiveField(null);
                      if (errors.location) setErrors((p) => ({ ...p, location: undefined }));
                    }}
                  >
                    <div className="icon-container col-1">
                      <i className="fa fa-map-marker" />
                    </div>
                    <div className="text-container col-11">
                      <span className="label1">{s.city}</span>
                      {sub && <span className="label2">{sub}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </label>

        {/* Restaurant / cuisine — autocomplete (dropdown scoped to the field).
            Disabled until a city is entered — city is mandatory, and every
            restaurant search below is scoped to it, so there's nothing
            meaningful to search until then. */}
        <label
          className={`search-field restaurant-suggest min-[1200px]:border-r-0 s-boder-none mb-0${
            cityEntered ? '' : ' opacity-50 cursor-not-allowed'
          }`}
        >
          <span className="sr-only">Restaurant</span>
          <i className="text-lg far fa-search" aria-hidden />
          <input
            className="search-input-restaurant p-0 disabled:cursor-not-allowed disabled:opacity-60"
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSuggestOpen(true);
              if (selectedRestaurantId) setSelectedRestaurantId('');
            }}
            onFocus={() => setSuggestOpen(true)}
            placeholder={cityEntered ? 'Search by Restaurant' : 'Enter a city first'}
            aria-label="Restaurant"
            aria-autocomplete="list"
            aria-expanded={showSuggest}
            // Restaurant-name search only makes sense scoped to a city —
            // require the location field to be filled in first. The
            // placeholder text stays the static "Search by Restaurant"
            // (per product ask) — the `title` tooltip is the only hint
            // given for why the field won't accept input yet.
            // disabled={!location.trim()}
            title={location.trim() ? undefined : 'Select a city first'}
          />
          {/* Clear button for the restaurant field — same behaviour as
              the location clear (keeps focus + reopens the suggest
              dropdown for the user to type a new query). */}
          {query && (
            <button
              type="button"
              aria-label="Clear restaurant"
              className="search-field__clear"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setQuery('');
                setRestaurantSuggestions([]);
                setSelectedRestaurantId('');
                setSuggestOpen(true);
              }}
            >
              <i className="fal fa-times" aria-hidden />
            </button>
          )}

          {showSuggest && (
            <div className="search-results-dropdown">
              {suggestLoading && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '1.5rem 0',
                  }}
                >
                  <div
                    className="spinner-border spinner-border-sm text-secondary"
                    role="status"
                  />
                </div>
              )}

              {!suggestLoading && totalSuggestions === 0 && (
                <span className="dropdown-item text-muted">No result found</span>
              )}

              {!suggestLoading && rankedRestaurants.length > 0 && (
                <>
                  {rankedRestaurants.map((s) => (
                    <button
                      key={`r-${s.id}`}
                      type="button"
                      // Exact class combo the city dropdown uses so the
                      // two panels render identically:
                      //   • `.ui-menu-item-wrapper` → padding 18px 26px
                      //   • `.ui-menu-item`         → border-top separator
                      //   • `.autocomplete-item`    → hover/focus bg
                      // A shared `:first-child` rule in restaurants.css
                      // strips the top border off the topmost row so
                      // the panel starts flush.
                      className="dropdown-item ui-menu-item autocomplete-item ui-menu-item-wrapper"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => pickSuggestion(s)}
                    >
                      {/* Two-column layout mirroring the city dropdown:
                          `icon-container` holds the restaurant's photo
                          (image swap on 404) in place of the map-pin
                          <i>, filling the full 32×32 circular container
                          so it reads as a real thumbnail rather than a
                          small icon; `text-container` carries the
                          restaurant name as `label1`. No subtitle today —
                          leaves room for a `label2` (e.g. cuisine) later. */}
                      <div className="icon-container col-1">
                        <img
                          src={s.image}
                          alt={s.name}
                          style={{
                            width: 18,
                            height: 18,
                            borderRadius: 3,
                            objectFit: 'contain',
                            flexShrink: 0,
                          }}
                          onError={(e) => { (e.target as HTMLImageElement).src = RESTAURANT_NO_IMAGE_URL; }}
                        />
                      </div>
                      <div className="text-container col-11">
                        <span className="label1">{s.name}</span>
                      </div>
                    </button>
                  ))}
                </>
              )}
            </div>
          )}
        </label>

        {/* Submit */}
        <div className="min-[1200px]:pl-3 min-[1200px]:pt-0">
          <button
            type="submit"
            className="primary-button h-[44px] w-full px-5 text-[16px] min-[1200px]:whitespace-nowrap res-mb-10"
          >
            Find a table
          </button>
        </div>
      </form>
    </section>
  );
}
